import axios from 'axios';
import api from './api';

// ---- Types ----

export interface SiteProcessType {
  id: string;
  siteId: string;
  parentId: string | null;
  name: string;
  order: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SiteLocation {
  id: string;
  siteId: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SiteDetailLocation {
  id: string;
  siteId: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SiteEquipment {
  id: string;
  siteId: string;
  name: string;
  managementNumber: string | null;
  processType: string | null;
  currentLocationId: string | null;
  currentLocation?: Pick<SiteLocation, 'id' | 'name'> | null;
  currentDetailLocationId?: string | null;
  currentDetailLocation?: Pick<SiteDetailLocation, 'id' | 'name'> | null;
  isActive: boolean;
  /** 법정 기종 코드(②-c2) · null = 기종 미선택(법정 판정 밖) */
  equipmentKindCode?: string | null;
  /** 그 기종의 규격 값 — 판정은 shared `judgeRegulatedEquipment` */
  specValues?: Record<string, unknown> | null;
  /** 설치(·등록)일 ISO */
  installedAt?: string | null;
  /** 이 장치가 배기하는 측정 공정 id(활성 — 국소배기장치 · 화학물질 마스터 설계 ⑦) · 목록 응답에만 */
  exhaustProcessIds?: string[];
  /**
   * 다른 사실 축이 채운 규격 값(`source` 필드 — 국소배기의 「최근 2년 노출기준 50% 미만」). 서버가 shared `deriveExhaustMeasurementFact`로
   * 만든 값이고 화면은 이것을 `judgeRegulatedEquipment`의 `externalFacts`로 넘긴다(안 넘기면 늘 UNKNOWN — 서버와 다른 판정). `null` = 그 기종 아님 ·
   * `{}` = 확인 필요(키 없음) · 목록 응답에만
   */
  externalFacts?: Record<string, unknown> | null;
  /** 그 사실의 사유(「배기 공정 미입력」·「측정 연결 없음: 유리규산」·「도장 × 벤젠 2026-05-01: 노출기준 초과」) · 목록 응답에만 */
  externalFactNotes?: string[];
  createdAt: string;
  updatedAt: string;
}


export interface CreateLocationDto {
  name: string;
}

export interface UpdateLocationDto {
  name?: string;
  isActive?: boolean;
}

export interface CreateDetailLocationDto {
  name: string;
}

export interface UpdateDetailLocationDto {
  name?: string;
  isActive?: boolean;
}

export interface CreateProcessTypeDto {
  name: string;
  parentId?: string | null;
}

export interface ReorderProcessTypeDto {
  parentId: string | null;
  orderedIds: string[];
}

export interface UpdateProcessTypeDto {
  name?: string;
  isActive?: boolean;
}

/** 법정 분류 칸(②-c2) — 생략은 «건드리지 않음» · null은 비움 */
interface EquipmentLegalFieldsDto {
  equipmentKindCode?: string | null;
  specValues?: Record<string, unknown> | null;
  installedAt?: string | null;
  /** 배기하는 측정 공정 id(국소배기장치 기종만 · 같은 현장의 활성 공정) · null·[] = 비움 */
  exhaustProcessIds?: string[] | null;
}

export interface CreateEquipmentDto extends EquipmentLegalFieldsDto {
  name: string;
  managementNumber?: string | null;
  processType?: string | null;
  currentLocationId?: string | null;
  currentDetailLocationId?: string | null;
}

export interface UpdateEquipmentDto extends EquipmentLegalFieldsDto {
  name?: string;
  managementNumber?: string | null;
  processType?: string | null;
  currentLocationId?: string | null;
  currentDetailLocationId?: string | null;
  isActive?: boolean;
}

// ---- ProcessType API ----

export async function getSiteProcessTypes(orgId: string, siteId: string): Promise<SiteProcessType[]> {
  const res = await api.get<{ success: boolean; data: SiteProcessType[] }>(
    `/organizations/${orgId}/sites/${siteId}/process-types`
  );
  return res.data.data;
}

/**
 * SiteProcessType[] (서버는 flat 배열로 응답, parentId로 부모/자식 구분)을 평탄화하여
 * SearchableSelect 옵션 형식으로 변환. 자식이 있는 부모는 옵션에 포함되지 않고
 * "부모 > 자식" 라벨로 자식 옵션만 노출. 자식이 없는 부모는 자체 옵션으로 노출.
 *
 * order 필드로 부모/자식 각각 정렬.
 */
export function flattenProcessTypeHierarchy(
  processTypes: SiteProcessType[],
): { id: string; name: string }[] {
  const activeItems = processTypes.filter((pt) => pt.isActive);
  const childrenByParent = new Map<string, SiteProcessType[]>();
  const topLevel: SiteProcessType[] = [];
  for (const pt of activeItems) {
    if (pt.parentId === null) {
      topLevel.push(pt);
    } else {
      const arr = childrenByParent.get(pt.parentId) ?? [];
      arr.push(pt);
      childrenByParent.set(pt.parentId, arr);
    }
  }
  topLevel.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  childrenByParent.forEach((arr) => arr.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)));

  const result: { id: string; name: string }[] = [];
  for (const parent of topLevel) {
    const children = childrenByParent.get(parent.id) ?? [];
    if (children.length === 0) {
      result.push({ id: parent.id, name: parent.name });
    } else {
      for (const child of children) {
        result.push({ id: child.id, name: `${parent.name} > ${child.name}` });
      }
    }
  }
  return result;
}

export async function createSiteProcessType(
  orgId: string,
  siteId: string,
  dto: CreateProcessTypeDto
): Promise<SiteProcessType> {
  const res = await api.post<{ success: boolean; data: SiteProcessType }>(
    `/organizations/${orgId}/sites/${siteId}/process-types`,
    dto
  );
  return res.data.data;
}

export async function updateSiteProcessType(
  orgId: string,
  siteId: string,
  processTypeId: string,
  dto: UpdateProcessTypeDto
): Promise<SiteProcessType> {
  const res = await api.patch<{ success: boolean; data: SiteProcessType }>(
    `/organizations/${orgId}/sites/${siteId}/process-types/${processTypeId}`,
    dto
  );
  return res.data.data;
}

export async function deleteSiteProcessType(
  orgId: string,
  siteId: string,
  processTypeId: string
): Promise<void> {
  await api.delete(`/organizations/${orgId}/sites/${siteId}/process-types/${processTypeId}`);
}

export async function reorderSiteProcessTypes(
  orgId: string,
  siteId: string,
  params: ReorderProcessTypeDto
): Promise<void> {
  await api.patch(
    `/organizations/${orgId}/sites/${siteId}/process-types/reorder`,
    params
  );
}

// ---- Location API ----

export async function getSiteLocations(orgId: string, siteId: string): Promise<SiteLocation[]> {
  const res = await api.get<{ success: boolean; data: SiteLocation[] }>(
    `/organizations/${orgId}/sites/${siteId}/locations`
  );
  return res.data.data;
}

export async function createSiteLocation(
  orgId: string,
  siteId: string,
  dto: CreateLocationDto
): Promise<SiteLocation> {
  const res = await api.post<{ success: boolean; data: SiteLocation }>(
    `/organizations/${orgId}/sites/${siteId}/locations`,
    dto
  );
  return res.data.data;
}

export async function updateSiteLocation(
  orgId: string,
  siteId: string,
  locationId: string,
  dto: UpdateLocationDto
): Promise<SiteLocation> {
  const res = await api.patch<{ success: boolean; data: SiteLocation }>(
    `/organizations/${orgId}/sites/${siteId}/locations/${locationId}`,
    dto
  );
  return res.data.data;
}

export async function deleteSiteLocation(
  orgId: string,
  siteId: string,
  locationId: string
): Promise<void> {
  await api.delete(`/organizations/${orgId}/sites/${siteId}/locations/${locationId}`);
}

// ---- Detail Location API ----

export async function getSiteDetailLocations(orgId: string, siteId: string): Promise<SiteDetailLocation[]> {
  const res = await api.get<{ success: boolean; data: SiteDetailLocation[] }>(
    `/organizations/${orgId}/sites/${siteId}/detail-locations`
  );
  return res.data.data;
}

export async function createSiteDetailLocation(
  orgId: string,
  siteId: string,
  dto: CreateDetailLocationDto
): Promise<SiteDetailLocation> {
  const res = await api.post<{ success: boolean; data: SiteDetailLocation }>(
    `/organizations/${orgId}/sites/${siteId}/detail-locations`,
    dto
  );
  return res.data.data;
}

export async function updateSiteDetailLocation(
  orgId: string,
  siteId: string,
  detailLocationId: string,
  dto: UpdateDetailLocationDto
): Promise<SiteDetailLocation> {
  const res = await api.patch<{ success: boolean; data: SiteDetailLocation }>(
    `/organizations/${orgId}/sites/${siteId}/detail-locations/${detailLocationId}`,
    dto
  );
  return res.data.data;
}

export async function deleteSiteDetailLocation(
  orgId: string,
  siteId: string,
  detailLocationId: string
): Promise<void> {
  await api.delete(`/organizations/${orgId}/sites/${siteId}/detail-locations/${detailLocationId}`);
}

export interface RecentPhotoContext {
  locationId: string | null;
  equipmentId: string | null;
  locationName: string | null;
  equipmentName: string | null;
  usedCount: number;
  lastUsedAt: string;
}

export async function getRecentPhotoContexts(
  orgId: string,
  siteId: string,
  limit = 5
): Promise<RecentPhotoContext[]> {
  const res = await api.get<{ success: boolean; data: RecentPhotoContext[] }>(
    `/organizations/${orgId}/sites/${siteId}/recent-photo-context?limit=${limit}`
  );
  return res.data.data;
}

// ---- Equipment API ----

export async function getSiteEquipment(orgId: string, siteId: string): Promise<SiteEquipment[]> {
  const res = await api.get<{ success: boolean; data: SiteEquipment[] }>(
    `/organizations/${orgId}/sites/${siteId}/equipment`
  );
  return res.data.data;
}

export async function createSiteEquipment(
  orgId: string,
  siteId: string,
  dto: CreateEquipmentDto
): Promise<SiteEquipment> {
  try {
    const res = await api.post<{ success: boolean; data: SiteEquipment }>(
      `/organizations/${orgId}/sites/${siteId}/equipment`,
      dto
    );
    return res.data.data;
  } catch (err: unknown) {
    if (axios.isAxiosError(err) && err.response?.status === 404) {
      throw new Error('장비 추가 기능은 현재 지원되지 않습니다.');
    }
    throw err;
  }
}

export async function updateSiteEquipment(
  orgId: string,
  siteId: string,
  equipmentId: string,
  dto: UpdateEquipmentDto
): Promise<SiteEquipment> {
  const res = await api.patch<{ success: boolean; data: SiteEquipment }>(
    `/organizations/${orgId}/sites/${siteId}/equipment/${equipmentId}`,
    dto
  );
  return res.data.data;
}

export async function deleteSiteEquipment(
  orgId: string,
  siteId: string,
  equipmentId: string
): Promise<void> {
  await api.delete(`/organizations/${orgId}/sites/${siteId}/equipment/${equipmentId}`);
}
